import { motion } from 'motion/react';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit';
  className?: string;
  icon?: ReactNode;
}

export default function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  type = 'button',
  className = '',
  icon,
}: ButtonProps) {
  const baseStyles =
    'relative overflow-hidden rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-2.5 touch-feedback disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap select-none';

  const variants = {
    primary:
      'btn-gradient text-white shadow-[0_0_20px_rgba(0,212,255,0.3)] hover:shadow-[0_0_30px_rgba(0,212,255,0.5)] active:shadow-[0_0_15px_rgba(0,212,255,0.4)]',
    secondary: 'glass text-white hover:bg-white/10 active:bg-white/5',
    ghost: 'bg-transparent text-white/70 hover:text-white hover:bg-white/5 active:bg-white/10',
  };

  const sizes = {
    sm: 'px-4 py-2.5 text-sm min-h-[40px]',
    md: 'px-6 py-3 text-base min-h-[48px]',
    lg: 'px-8 py-4 text-lg min-h-[56px]',
  };

  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 20,
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {/* Shimmer effect for primary */}
      {variant === 'primary' && !loading && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full"
          animate={{ translateX: ['-100%', '200%'] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        />
      )}

      {/* Loading state */}
      {loading ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-2.5"
        >
          <Loader2 size={iconSizes[size]} className="animate-spin" />
          <span className="relative z-10">{children}</span>
        </motion.div>
      ) : (
        <>
          {icon && (
            <span className="relative z-10 flex items-center justify-center">
              {icon}
            </span>
          )}
          <span className="relative z-10">{children}</span>
        </>
      )}
    </motion.button>
  );
}
